import type { ReactNode } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { EllipsisVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import type { GuidelineResponseDto } from '@/lib/api/guidelines-client';
import { usePermissions } from '@/entities/user';
import { useTranslation } from 'react-i18next';

export interface GuidelineActionMenuProps {
  guideline: GuidelineResponseDto;
  onEdit?: (guideline: GuidelineResponseDto) => void;
  onDelete?: (guideline: GuidelineResponseDto) => void;
}

/**
 * A disabled menu item swallows pointer events, so the tooltip has to listen on
 * a wrapper rather than the item itself. When the action is available there is
 * nothing to explain, so the wrapper is skipped entirely.
 */
const WithPermissionTooltip = ({
  disabled,
  tooltip,
  children,
}: {
  disabled: boolean;
  tooltip: string;
  children: ReactNode;
}) => {
  if (!disabled) return children;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block">{children}</span>
      </TooltipTrigger>
      <TooltipContent side="left">{tooltip}</TooltipContent>
    </Tooltip>
  );
};

export const GuidelineActionMenu = ({ guideline, onEdit, onDelete }: GuidelineActionMenuProps) => {
  const { t } = useTranslation('knowledge');

  const { canManageBrandVisualGuidelines } = usePermissions();
  const cannotManage = !canManageBrandVisualGuidelines;
  const noPermissionTooltip = t('guidelines.guidelineActionMenu.youDonTHavePermission');

  return (
    <DropdownMenu>
      {/*
       * The trigger is revealed by the row it sits in (`group` on the row), and it
       * has to stay focusable so keyboard users can reach it while it is faded.
       */}
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('guidelines.guidelineActionMenu.guidelineActions')}
          className="opacity-0 transition-opacity duration-base ease-out focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
        >
          <EllipsisVerticalIcon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <TooltipProvider>
          <WithPermissionTooltip disabled={cannotManage} tooltip={noPermissionTooltip}>
            <DropdownMenuItem disabled={cannotManage} onSelect={() => onEdit?.(guideline)}>
              <PencilIcon />
              {t('guidelines.guidelineActionMenu.edit')}
            </DropdownMenuItem>
          </WithPermissionTooltip>
          <WithPermissionTooltip disabled={cannotManage} tooltip={noPermissionTooltip}>
            <DropdownMenuItem
              variant="destructive"
              disabled={cannotManage}
              onSelect={() => onDelete?.(guideline)}
            >
              <Trash2Icon />
              {t('guidelines.guidelineActionMenu.delete')}
            </DropdownMenuItem>
          </WithPermissionTooltip>
        </TooltipProvider>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

GuidelineActionMenu.displayName = 'GuidelineActionMenu';
